import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { PropsOf } from '@headlessui/react/dist/types';
import { twMerge } from 'tailwind-merge';
import { FaCheckCircle, FaExclamationTriangle, FaRedo, FaTrash } from 'react-icons/fa';
import { CgSpinner } from 'react-icons/cg';
import { TbCloudDownload, TbLink, TbLinkOff, TbChevronLeft } from 'react-icons/tb';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { isSameDay } from 'date-fns';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';

import PageHeader from '../../components/misc/PageHeader';
import SearchBar from '../../components/forms/SearchBar';
import Labeled from '../../components/misc/Labeled';
import useSelectedBusiness from '../../../hooks/business/useSelectedBusiness';
import {
  BaseTable,
  BaseTableBody,
  BaseTableDataRow,
  BaseTableHeader,
  BaseTableHeaderRow,
  BaseTableSpacerRow,
} from '../../components/tables/BaseTable';
import EmptyScreenView from '../../components/misc/EmptyScreenView';
import * as bankAccountService from '../../../services/bankAccountService';
import {
  BankAccount,
  BankMutation,
  BankTransactionWithParticipation,
  CreateBankTransactionModel,
  GoCardlessInstitution,
  TransactionType,
} from '../../../types/bankAccount';
import ModalLayout from '../../components/layouts/ModalLayout';
import Button from '../../components/buttons/Button';
import { formatDate } from '../../../utils/stringUtils';
import AssignSplitTransactionModal from '../../components/modals/AssignSplitTransactionModal';
import TransactionModal from '../../components/modals/TransactionModal';
import DeclarationModal from '../../components/modals/DeclarationModal';
import {
  buildCombinedTransactionModels,
  buildTransactionFromMutation,
  formatEuroCents,
  formatEuroCentsWithSign,
  matchMutationsToConnections,
} from '../../../utils/bankAccountUtil';
import DropdownSelect from '../../components/forms/DropdownSelect';
import MutationIntoTransactionsModal from '../../components/modals/MutationIntoTransactionsModal';
import WithTooltip from '../../components/misc/WithTooltip';
import DatePicker from '../../components/forms/DatePicker';
import { RootState } from '../../../redux/reducers';
import { Connection, ConnectionType } from '../../../redux/slices/connectionsSlice';
import { Event, EventParticipationAnswer } from '../../../types/event';

import VerifyTransactionMatchesView from '../../components/financial/VerifyTransactionMatchesView';
import VerifyContributionPlanMatchesView from '../../components/financial/VerifyContributionPlanMatchesView';
import VerifyEventExpenseMatchesView from '../../components/financial/VerifyEventExpenseMatchesView';
import VerifyInternalMutationMatchesView from '../../components/financial/VerifyInternalMutationMatchesView';
import HandleUnmatchedTransactionsView from '../../components/financial/HandleUnmatchedTransactionsView';

interface MutationsPageProps {
  hideHeader?: boolean;
}

export default function MutationsPage({ hideHeader = false }: MutationsPageProps): JSX.Element {
  const { t, i18n } = useTranslation('', { keyPrefix: 'page.financial.accountEntry' });
  const params = useParams();
  const navigate = useNavigate();
  const [search, setSearch] = useState<string>('');
  const [bankAccount, setBankAccount] = useState<BankAccount | null>();
  const [allBankAccounts, setAllBankAccounts] = useState<BankAccount[]>();
  const [mutations, setMutations] = useState<BankMutation[] | null | undefined>(null);

  // EXCEL UPLOAD
  const [importModalOpen, setImportModalOpen] = useState<boolean>(false);
  const [transactionModalOpen, setTransactionModalOpen] = useState<boolean>(false);
  const [selectedMutation, setSelectedMutation] = useState<BankMutation | null>(null);
  const [splitModalMutationOpen, setSplitModalMutationOpen] = useState<boolean>(false);
  const [splitModalTransactionsOpen, setSplitModalTransactionsOpen] = useState<boolean>(false);
  const [cardlessModalOpen, setCardlessModalOpen] = useState<boolean>(false);
  const [splitModalMutation, setSplitModalMutation] = useState<BankMutation | null>(null);
  const [splitModalTransactions, setSplitModalTransactions] = useState<
    CreateBankTransactionModel[] | null
  >(null);
  const [filter, setFilter] = useState<'ALL' | 'PROCESSED' | 'UNPROCESSED'>('ALL');
  const [fetchingLoading, setFetchingLoading] = useState<boolean>(false);
  const [tmInitialProcessType] = useState<'ASSIGN' | 'SPLIT' | 'INTERNAL'>();
  const [tmInitialInternalSide] = useState<'FROM' | 'TO'>();

  // When opening a processed mutation in edit mode, store the existing transaction
  const [selectedExistingTransaction, setSelectedExistingTransaction] =
    useState<BankTransactionWithParticipation | null>(null);

  // Store confirmed matches from bulk modal for use in individual TransactionModal
  const [confirmedMatches, setConfirmedMatches] = useState<
    | {
        transaction: CreateBankTransactionModel;
        matchedConnection: Connection;
        contributionPlanId?: number;
      }[]
    | null
  >(null);

  const business = useSelectedBusiness();

  // FIXME
  // const { items: transactionsPerPage, ...pagination } = usePagination(bankAccount?.transactions ?? []);

  useEffect(() => {
    bankAccountService.getAllBankAccounts().then((bs) => {
      setAllBankAccounts(bs);

      // If we have a specific bank account ID from the URL, use that
      if (params.bankAccountId) {
        const specificAccount = bs.find(
          (account) => account.accountId.toString() === params.bankAccountId,
        );
        if (specificAccount) {
          setBankAccount(specificAccount);
        }
      } else if (bs.length > 0) {
        // Default behavior: use first account
        setBankAccount(bs[0]);
      }
    });
  }, [business, params.bankAccountId]);

  useEffect(() => {
    if (!bankAccount) {
      return;
    }

    setMutations(undefined);

    bankAccountService.getAllMutationsForBank(bankAccount.accountId).then(setMutations);
  }, [bankAccount]);

  useEffect(() => {
    if (!splitModalTransactionsOpen) {
      setSplitModalTransactions(null);
    }
  }, [splitModalTransactionsOpen]);

  useEffect(() => {
    if (!transactionModalOpen) {
      setSelectedMutation(null);
    }
  }, [transactionModalOpen]);

  const createBankAccountTransactions = (
    bulkConfirmedMatches: {
      transaction: CreateBankTransactionModel;
      matchedConnection: Connection;
      contributionPlanId?: number;
    }[],
    selectedGroupMutations: CreateBankTransactionModel[],
  ) => {
    const combined = buildCombinedTransactionModels(bulkConfirmedMatches, selectedGroupMutations);

    bankAccountService.createTransactionsWithParticipations(combined).then(() => {
      //set the mutations like below but
      setMutations((prevMutations) => {
        if (!prevMutations) return prevMutations;
        // For each transaction in combined, update the corresponding mutation
        let updatedMutations = [...prevMutations];
        combined.forEach((tns) => {
          updatedMutations = updatedMutations.map((m) =>
            m.mutationId === tns.mutationId
              ? {
                  ...m,
                  bankTransactionCount: m.bankTransactionCount + 1,
                }
              : m,
          );
        });
        return updatedMutations;
      });
      toast.success(t('success.importBankTransactions'));
      setImportModalOpen(false);
    });
  };

  const handleCreateGoCardlessLink = async (
    bankId: string,
    maxHistoricalDaysPreference?: number,
  ) => {
    if (!bankAccount || !bankAccount?.accountId) {
      toast.error(t('error.createGoCardlessLink'));
      return;
    }

    await bankAccountService
      .createGoCardlessLink(
        bankAccount.accountId,
        bankId,
        i18n.language as 'en' | 'nl',
        maxHistoricalDaysPreference,
      )
      .then((linkData) => {
        const dummyAnchor = document.createElement('a');
        dummyAnchor.href = linkData.link;
        dummyAnchor.click();
        dummyAnchor.remove();
      })
      .catch((err) => {
        console.error('Error creating GoCardless link:', err);
        toast.error(t('error.createGoCardlessLink'));
      });
  };

  const unlinkAccount = async () => {
    if (!bankAccount || !bankAccount?.accountId) {
      toast.error(t('error.unlinkGoCardlessAccount'));
      return;
    }

    bankAccountService
      .unlinkAccountFromGoCardless(bankAccount.accountId)
      .then(() => {
        toast.success(t('success.unlinkGoCardlessAccount'));
        setBankAccount({ ...bankAccount!, requisitionId: undefined });
      })
      .catch(() => {
        toast.error(t('error.unlinkGoCardlessAccount'));
      });
  };

  const refetchMutations = async () => {
    if (!bankAccount || !bankAccount.accountId || !bankAccount.goCardlessAccountId) {
      toast.error(t('error.refetchMutations'));
      return;
    }

    setFetchingLoading(true);

    bankAccountService
      .fetchLatestGoCardlessTransactions(bankAccount.accountId)
      .then(() => {
        setBankAccount((prev) =>
          prev
            ? {
                ...prev,
                latestFetching: new Date(),
              }
            : prev,
        );

        return bankAccountService.getAllMutationsForBank(bankAccount.accountId).then(setMutations);
      })
      .then(() => toast.success(t('success.refetchMutations')))
      .catch(() => toast.error(t('error.refetchMutations')))
      .finally(() => setFetchingLoading(false));
  };

  const deleteBankAccount = async () => {
    if (!bankAccount || !bankAccount?.accountId) {
      toast.error('No bank account selected');
      return;
    }

    if (
      window.confirm(
        'Are you sure you want to delete this bank account? This action cannot be undone.',
      )
    ) {
      try {
        await bankAccountService.deleteBankAccount(bankAccount.accountId);
        toast.success('Bank account deleted successfully');
        // Navigate back to overview since the account is deleted
        navigate('/financial/administration');
      } catch (error) {
        toast.error('Failed to delete bank account');
      }
    }
  };

  return (
    <>
      {!hideHeader ? (
        <>
          {params.bankAccountId && (
            <Link to="/financial/administration">
              <Button className="mb-2 gap-1 p-0">
                <TbChevronLeft size={20} /> Back
              </Button>
            </Link>
          )}
          <PageHeader
            title={t('title')}
            badge={
              bankAccount ? (
                <div className="rounded-full bg-primary-300 px-4 py-2">
                  <p className="text-sm font-medium">
                    {bankAccount.iban} ({formatEuroCents(bankAccount.balance, i18n.language)})
                  </p>
                </div>
              ) : undefined
            }
            subtitle={t('subtitle')}
          />
          {params.bankAccountId && (
            <div className="mb-4 flex items-center justify-end">
              <div className="flex items-center gap-2">
                {bankAccount?.requisitionId ? (
                  bankAccount?.transferCompleted ? (
                    <>
                      <WithTooltip
                        className="w-72 whitespace-normal text-center"
                        tooltip={t('refetchTooltip')}>
                        <Button
                          variant="secondary"
                          disabled={
                            fetchingLoading ||
                            (bankAccount.latestFetching &&
                              isSameDay(bankAccount.latestFetching, new Date()))
                          }
                          onClick={() => refetchMutations()}>
                          {fetchingLoading ? (
                            <CgSpinner className="animate-spin" />
                          ) : (
                            <TbCloudDownload />
                          )}
                          {t('refetchButton')}
                        </Button>
                      </WithTooltip>
                      <Button variant="danger-v3" onClick={() => unlinkAccount()}>
                        <TbLinkOff />
                        {t('unlinkButton')}
                      </Button>
                      <Button variant="danger-v3" onClick={() => deleteBankAccount()}>
                        <FaTrash />
                        Delete
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button onClick={() => unlinkAccount()} variant="primary-dark">
                        <FaRedo />
                        {t('setupFailedButton')}
                      </Button>
                      <Button variant="danger-v3" onClick={() => deleteBankAccount()}>
                        <FaTrash />
                        Delete
                      </Button>
                    </>
                  )
                ) : (
                  <>
                    <Button variant="primary-dark" onClick={() => setCardlessModalOpen(true)}>
                      <TbLink />
                      {t('linkButton')}
                    </Button>
                    <Button variant="danger-v3" onClick={() => deleteBankAccount()}>
                      <FaTrash />
                      Delete
                    </Button>
                  </>
                )}
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="mb-4 flex items-center justify-between">
          {params.bankAccountId ? (
            // Show back button when viewing specific bank account
            <Link to="/financial/administration">
              <Button className="gap-1 p-0">
                <TbChevronLeft size={20} /> Back
              </Button>
            </Link>
          ) : (
            // Show dropdown when viewing all accounts
            <div className="flex items-center gap-2">
              <p className="font-medium">Select bank account:</p>
              <DropdownSelect
                options={(allBankAccounts ?? []).map((v) => ({ value: v, label: v.iban }))}
                setValue={setBankAccount}
                value={bankAccount}
                className="my-2 w-72"
              />
            </div>
          )}
          <div className="flex items-center gap-2">
            {bankAccount?.requisitionId ? (
              bankAccount?.transferCompleted ? (
                <>
                  <WithTooltip
                    className="w-72 whitespace-normal text-center"
                    tooltip={t('refetchTooltip')}>
                    <Button
                      variant="secondary"
                      disabled={
                        fetchingLoading ||
                        (bankAccount.latestFetching &&
                          isSameDay(bankAccount.latestFetching, new Date()))
                      }
                      onClick={() => refetchMutations()}>
                      {fetchingLoading ? (
                        <CgSpinner className="animate-spin" />
                      ) : (
                        <TbCloudDownload />
                      )}
                      {t('refetchButton')}
                    </Button>
                  </WithTooltip>
                  <Button variant="danger-v3" onClick={() => unlinkAccount()}>
                    <TbLinkOff />
                    {t('unlinkButton')}
                  </Button>
                  <Button variant="danger-v3" onClick={() => deleteBankAccount()}>
                    <FaTrash />
                    Delete
                  </Button>
                </>
              ) : (
                <>
                  <Button onClick={() => unlinkAccount()} variant="primary-dark">
                    <FaRedo />
                    {t('setupFailedButton')}
                  </Button>
                  <Button variant="danger-v3" onClick={() => deleteBankAccount()}>
                    <FaTrash />
                    Delete
                  </Button>
                </>
              )
            ) : (
              <>
                <Button variant="primary-dark" onClick={() => setCardlessModalOpen(true)}>
                  <TbLink />
                  {t('linkButton')}
                </Button>
                <Button variant="danger-v3" onClick={() => deleteBankAccount()}>
                  <FaTrash />
                  Delete
                </Button>
              </>
            )}
          </div>
        </div>
      )}

      <div className="flex flex-1 flex-col gap-5 pb-4">
        {mutations && (
          <>
            {mutations && false && (
              <div className="flex flex-col gap-4 rounded-xl bg-secondary p-4">
                <div>
                  <h3 className="text-lg font-medium">Warning!</h3>
                  <p>
                    {t('warningMessageSplit', {
                      count: 0, // mutations.filter((m) => !m).length,
                    })}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Link to="/financial/bulk-split">
                    <Button variant="primary-dark" className="text-sm">
                      {t('bulkSplit')}
                    </Button>
                  </Link>
                  <p>{t('manuallySplit')}</p>
                </div>
              </div>
            )}
            <div className="flex items-center justify-between">
              <SearchBar className="max-w-[380px]" search={search} setSearch={setSearch} />
            </div>
          </>
        )}
        {mutations === undefined ? (
          <EmptyScreenView title={t('loading')} subtitle="" />
        ) : mutations === null ? (
          <section className="mt-8 flex w-full flex-col items-center justify-center gap-4">
            <img
              src="/assets/figures/phone_hi.svg"
              alt="vera mess"
              className="w-1/4 max-md:w-1/2"
            />
            <h2 className="mt-2 text-center text-2xl">{t('selectBankAccountOrCreateOne')}</h2>
          </section>
        ) : (
          <>
            {mutations.length === 0 ? (
              <EmptyScreenView />
            ) : (
              <>
                <div className="flex items-center gap-2">
                  <p>{t('filter')}</p>
                  <Button
                    onClick={() =>
                      setFilter((prev) => (prev === 'PROCESSED' ? 'ALL' : 'PROCESSED'))
                    }
                    className={`${filter === 'PROCESSED' ? 'bg-success' : 'bg-secondary-200'} text-sm text-white`}>
                    {t('processed')}
                  </Button>
                  <Button
                    onClick={() =>
                      setFilter((prev) => (prev === 'UNPROCESSED' ? 'ALL' : 'UNPROCESSED'))
                    }
                    className={`${filter === 'UNPROCESSED' ? 'bg-orange-500' : 'bg-secondary-200'} text-sm text-white`}>
                    {t('unprocessed')}
                  </Button>
                </div>
                <div className="flex flex-col gap-8">
                  <div className="relative w-full overflow-x-auto">
                    <BaseTable>
                      <BaseTableHeader>
                        <BaseTableHeaderRow>
                          <HeaderCell>{t('table.date')}</HeaderCell>
                          <HeaderCell>{t('table.description')}</HeaderCell>
                          <HeaderCell>{t('table.amount')}</HeaderCell>
                          <HeaderCell>{t('table.balanceAfter')}</HeaderCell>
                          <HeaderCell>{t('table.transactions')}</HeaderCell>
                          <HeaderCell>{t('table.status')}</HeaderCell>
                        </BaseTableHeaderRow>
                      </BaseTableHeader>
                      <BaseTableBody>
                        <BaseTableSpacerRow />
                        {mutations
                          .filter((tns) => {
                            // First apply the filter (ALL, PROCESSED, UNPROCESSED)
                            if (filter === 'ALL') {
                              // For ALL, we still need to check if it matches the search
                            } else if (filter === 'PROCESSED') {
                              if (tns.bankTransactionCount === 0) return false;
                            } else if (filter === 'UNPROCESSED') {
                              if (tns.bankTransactionCount > 0) return false;
                            }

                            // Then apply search filtering
                            if (search.trim()) {
                              const searchLower = search.toLowerCase();
                              return (
                                tns.oppositeName?.toLowerCase().includes(searchLower) ||
                                tns.description?.toLowerCase().includes(searchLower)
                              );
                            }

                            return true;
                          })
                          .map((m, i) => (
                            <BaseTableDataRow
                              className="group cursor-pointer transition-all hover:bg-secondary-200"
                              onClick={async () => {
                                setSelectedMutation(m);
                                // If processed, fetch existing transaction(s) to open in edit mode
                                if (m.bankTransactionCount > 0) {
                                  try {
                                    const allTransactions =
                                      await bankAccountService.getAllTransactions();
                                    const existing = allTransactions.find(
                                      (txn) => txn.mutationId === m.mutationId,
                                    );
                                    setSelectedExistingTransaction(existing ?? null);
                                  } catch (e) {
                                    setSelectedExistingTransaction(null);
                                  }
                                } else {
                                  setSelectedExistingTransaction(null);
                                }
                                setTransactionModalOpen(true);
                              }}
                              key={`${m.sequenceNumber}_tr_${i}`}>
                              <DataCell>{formatDate(new Date(m.transactionDate))}</DataCell>
                              <DataCell>
                                <p className="text-sm">{m.oppositeName}</p>
                                <p className="text-xs text-gray-400">
                                  {m.description.slice(0, 50)}
                                  {m.description.length >= 50 && '...'}
                                </p>
                              </DataCell>
                              <DataCell>
                                {formatEuroCentsWithSign(m.amount, i18n.language)}
                              </DataCell>
                              <DataCell>{formatEuroCents(m.balanceAfter, i18n.language)}</DataCell>
                              <DataCell>
                                {m.bankTransactionCount > 0 ? (
                                  <span className="text-sm font-medium text-gray-700">
                                    {m.bankTransactionCount}
                                  </span>
                                ) : (
                                  <span className="text-sm text-gray-400">-</span>
                                )}
                              </DataCell>
                              <DataCell>
                                <div className="flex items-center gap-2">
                                  {m.bankTransactionCount === 0 ? (
                                    <div className="flex w-fit items-center gap-2 rounded-full bg-orange-500 px-3 py-1 text-white">
                                      <FaExclamationTriangle /> {t('unprocessed')}
                                    </div>
                                  ) : (
                                    <div className="flex w-fit items-center gap-2 rounded-full bg-success px-3 py-1 text-white">
                                      <FaCheckCircle /> {t('processed')}
                                    </div>
                                  )}
                                  {/* Row click opens modal; no inline action buttons */}
                                </div>
                              </DataCell>
                            </BaseTableDataRow>
                          ))}
                      </BaseTableBody>
                      {/*<BaseTablePagination pagination={pagination} />*/}
                    </BaseTable>
                  </div>
                </div>
              </>
            )}
          </>
        )}

        {bankAccount && (
          <AssignBulkMutationModal
            unassignedMutations={mutations?.filter((m) => m.bankTransactionCount === 0) ?? []}
            open={importModalOpen}
            setOpen={setImportModalOpen}
            onParseSuccess={createBankAccountTransactions}
            setParentConfirmedMatches={setConfirmedMatches}
            onUpdateMutations={setMutations}
          />
        )}

        {bankAccount && (
          <LinkBankAccountModal
            open={cardlessModalOpen}
            setOpen={setCardlessModalOpen}
            onBankChosen={handleCreateGoCardlessLink}
          />
        )}

        {selectedMutation && selectedExistingTransaction && selectedExistingTransaction.payerId && (
          <DeclarationModal
            open={transactionModalOpen}
            setOpen={setTransactionModalOpen}
            initialTransaction={(() => {
              // In declaration edit mode, use existing transaction
              return selectedExistingTransaction as unknown as CreateBankTransactionModel;
            })()}
            onSplitSuccess={(tns: CreateBankTransactionModel | CreateBankTransactionModel[]) => {
              if (Array.isArray(tns)) {
                toast.error('Splitting declarations in edit mode is not supported yet');
                setTransactionModalOpen(false);
                setSelectedExistingTransaction(null);
                setSelectedMutation(null);
                return;
              }

              // Update the declaration transaction
              bankAccountService
                .updateBankTransaction(selectedExistingTransaction!.transactionId, tns)
                .then(() => {
                  setTransactionModalOpen(false);
                  setSelectedExistingTransaction(null);
                  setSelectedMutation(null);
                  toast.success('Transaction updated');
                })
                .catch(() => toast.error('Failed to update transaction'));
            }}
          />
        )}

        {selectedMutation &&
          (!selectedExistingTransaction || !selectedExistingTransaction.payerId) && (
            <TransactionModal
              open={transactionModalOpen}
              setOpen={setTransactionModalOpen}
              initialTransaction={(() => {
                // If this mutation is processed and we fetched an existing transaction, open in edit mode
                if (selectedExistingTransaction) {
                  return selectedExistingTransaction as unknown as CreateBankTransactionModel;
                }

                // Check if this mutation was already matched in the bulk modal
                const confirmedMatch = confirmedMatches?.find(
                  (match: {
                    transaction: CreateBankTransactionModel;
                    matchedConnection: Connection;
                    contributionPlanId?: number;
                  }) => match.transaction.mutationId === selectedMutation.mutationId,
                );

                if (confirmedMatch) {
                  // Use the already matched information
                  const baseTransaction = buildTransactionFromMutation(selectedMutation);

                  if (confirmedMatch.contributionPlanId) {
                    // Include contribution payment if plan ID exists
                    return {
                      ...baseTransaction,
                      contributionPayment: {
                        connectionId: +confirmedMatch.matchedConnection.id,
                        contributionPlanId: confirmedMatch.contributionPlanId,
                      },
                    };
                  }
                  // If no contribution plan ID, just return the base transaction
                  // The TransactionModal will handle the connection matching separately
                }

                // Otherwise use the basic transaction model
                return buildTransactionFromMutation(selectedMutation);
              })()}
              mutation={selectedMutation}
              initialProcessType={tmInitialProcessType}
              initialInternalSide={tmInitialInternalSide}
              onInternalCreate={({ fromMutationId, toMutationId, description }) => {
                bankAccountService
                  .createInternalTransferFromMutations(fromMutationId, toMutationId, description)
                  .then(() => {
                    setMutations((prev) => {
                      if (!prev) return prev;
                      // Mark both source mutations as processed
                      return prev.map((m) =>
                        m.mutationId === fromMutationId || m.mutationId === toMutationId
                          ? { ...m, bankTransactionCount: m.bankTransactionCount + 1 }
                          : m,
                      );
                    });
                    setTransactionModalOpen(false);
                    setSelectedMutation(null);
                    toast.success('Internal transfer created');
                  })
                  .catch(() => toast.error('Failed to create internal transfer'));
              }}
              // onOpenCounterpart removed per request
              onSplitSuccess={(tns: CreateBankTransactionModel | CreateBankTransactionModel[]) => {
                if (Array.isArray(tns)) {
                  // Splitting in edit mode not supported; in create flow we handle via AssignSplitTransactionModal
                  if (selectedExistingTransaction) {
                    toast.error('Splitting transactions in edit mode is not supported yet');
                    setTransactionModalOpen(false);
                    setSelectedExistingTransaction(null);
                    setSelectedMutation(null);
                    return;
                  }

                  // Create flow - pass them to AssignSplitTransactionModal
                  setTransactionModalOpen(false);
                  setSelectedMutation(null);
                  setSplitModalTransactionsOpen(true);
                  setSplitModalTransactions(tns);
                  return;
                }

                // Single transaction
                if (selectedExistingTransaction) {
                  // Edit flow: update existing transaction
                  bankAccountService
                    .updateBankTransaction(selectedExistingTransaction.transactionId, tns)
                    .then(() => {
                      setTransactionModalOpen(false);
                      setSelectedExistingTransaction(null);
                      setSelectedMutation(null);
                      toast.success('Transaction updated');
                    })
                    .catch(() => toast.error('Failed to update transaction'));
                  return;
                }

                // Create flow: create new transaction
                bankAccountService.createTransactionWithParticipations(tns).then(() => {
                  setMutations((prevMutations) => {
                    return (
                      prevMutations?.map((m) =>
                        m.mutationId === tns.mutationId
                          ? {
                              ...m,
                              bankTransactionCount: m.bankTransactionCount + 1,
                            }
                          : m,
                      ) ?? prevMutations
                    );
                  });
                  setTransactionModalOpen(false);
                  setSelectedMutation(null);
                });
              }}
            />
          )}

        {splitModalMutation && (
          <MutationIntoTransactionsModal
            open={splitModalMutationOpen}
            setOpen={(open) => {
              if (!open) {
                setSplitModalMutationOpen(false);
                setTimeout(() => setSplitModalMutation(null), 200);
              }
            }}
            mutation={splitModalMutation}
            onTransactionSplit={(tns) => {
              setSplitModalMutationOpen(false);
              setTimeout(() => setSplitModalMutation(null), 200);
              setSplitModalTransactionsOpen(true);
              setSplitModalTransactions(tns);
            }}
          />
        )}
        {splitModalTransactions && splitModalTransactions.length > 0 && (
          <AssignSplitTransactionModal
            open={splitModalTransactionsOpen}
            setOpen={setSplitModalTransactionsOpen}
            transaction={splitModalTransactions[0]}
            mutation={splitModalMutation || selectedMutation || undefined}
            onAssignSuccess={(tns: CreateBankTransactionModel) => {
              bankAccountService.createTransactionWithParticipations(tns).then(() => {
                setSplitModalTransactions((prevTns) => (prevTns ? prevTns.slice(1) : null));
                setMutations((prevMutations) => {
                  return (
                    prevMutations?.map((m) =>
                      m.mutationId === tns.mutationId
                        ? {
                            ...m,
                            bankTransactionCount: m.bankTransactionCount + 1,
                          }
                        : m,
                    ) ?? prevMutations
                  );
                });
              });
            }}
          />
        )}
      </div>
    </>
  );
}

function HeaderCell({ className, ...props }: PropsOf<'th'>): JSX.Element {
  return <th className={twMerge('py-3 pl-1 text-primary-900', className)} {...props} />;
}

function DataCell({ className, ...props }: PropsOf<'td'>): JSX.Element {
  return (
    <td
      className={twMerge(
        'whitespace-nowrap py-4 pl-2 text-sm font-medium text-primary-900',
        className,
      )}
      {...props}
    />
  );
}

function LinkBankAccountModal({
  open,
  setOpen,
  onBankChosen,
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
  onBankChosen: (bankId: string, maxHistoricalDaysPreference?: number) => Promise<void>;
}): JSX.Element {
  const { t: tLink } = useTranslation('', {
    keyPrefix: 'page.financial.accountEntry.linkBankModal',
  });
  const [institutions, setInstitutions] = useState<GoCardlessInstitution[]>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedBank, setSelectedBank] = useState<GoCardlessInstitution | null>(null);
  const [startDate, setStartDate] = useState<Date>();

  useEffect(() => {
    if (open && !institutions) {
      bankAccountService.getDutchBanks().then((banks) => {
        setInstitutions(banks);
      });
    }
  }, [open, institutions]);

  const handleBankChosen = (instId: string, daysBack?: number) => {
    setIsLoading(true);
    setTimeout(() => {
      onBankChosen(instId, daysBack).finally(() => setIsLoading(false));
    });
  };

  return (
    <ModalLayout
      open={open}
      setOpen={setOpen}
      closeButton
      className="z-50 mx-6 my-8 w-full rounded-[20px] bg-secondary-50 p-8 sm:max-w-[700px]">
      <div>
        <h2 className="mb-4 text-lg font-semibold">{tLink('selectBank')}</h2>
        <div className="flex max-h-[calc(100vh-20rem)] flex-col gap-2 overflow-y-auto">
          {institutions && !isLoading ? (
            <>
              {institutions.map((inst) => (
                <button
                  key={inst.id}
                  className={`mb-2 flex w-full items-center rounded-md border-2 border-secondary-200 ${
                    inst.id === selectedBank?.id ? 'bg-secondary-200' : 'bg-secondary-50'
                  } p-4 hover:bg-secondary-200`}
                  type="button"
                  onClick={() => setSelectedBank(inst)}>
                  <div className="flex items-center gap-2">
                    <img
                      src={inst.logo}
                      alt={`${inst.name} logo`}
                      className="h-8 w-8 object-contain"
                    />
                    <p>{inst.name}</p>
                  </div>
                </button>
              ))}
              {selectedBank && (
                <Labeled label={tLink('startDay')}>
                  <DatePicker
                    date={startDate}
                    setDate={setStartDate}
                    minDate={dayjs().subtract(
                      selectedBank.transaction_total_days
                        ? +selectedBank.transaction_total_days
                        : 90,
                      'days',
                    )}
                    maxDate={dayjs()}
                    includeTime={false}
                  />
                  <p className="text-sm text-gray-500">{tLink('startDayHelper')}</p>
                </Labeled>
              )}
              <Button
                variant="primary-dark"
                className="mt-4 self-end"
                disabled={!selectedBank}
                onClick={() =>
                  handleBankChosen(
                    selectedBank!.id,
                    startDate ? Math.abs(dayjs().diff(dayjs(startDate), 'day')) + 1 : undefined,
                  )
                }>
                {tLink('linkButton')}
              </Button>
            </>
          ) : (
            <CgSpinner size={40} className="animate-spin text-primary-300" />
          )}
        </div>
      </div>
    </ModalLayout>
  );
}

function AssignBulkMutationModal({
  open,
  setOpen,
  unassignedMutations,
  onParseSuccess,
  setParentConfirmedMatches,
  onUpdateMutations,
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
  unassignedMutations: BankMutation[];
  onParseSuccess: (
    confirmedMatches: {
      transaction: CreateBankTransactionModel;
      matchedConnection: Connection;
      contributionPlanId?: number;
    }[],
    selectedGroupMutations: CreateBankTransactionModel[],
  ) => void;
  setParentConfirmedMatches: React.Dispatch<
    React.SetStateAction<
      | {
          transaction: CreateBankTransactionModel;
          matchedConnection: Connection;
          contributionPlanId?: number;
        }[]
      | null
    >
  >;
  onUpdateMutations: React.Dispatch<React.SetStateAction<BankMutation[] | null | undefined>>;
}): JSX.Element {
  const { t } = useTranslation();
  const [step, setStep] = React.useState(0);
  // matchMutationsToConnections
  const [matchedTransactions, setMatchedTransactions] = React.useState<Array<{
    mutation: CreateBankTransactionModel;
    matchedConnection?: Connection;
  }> | null>(null);

  const [internalMutationMatches, setInternalMutationMatches] = React.useState<
    | {
        mutation: CreateBankTransactionModel;
        counterpartMutation: BankMutation;
        confirmed: boolean;
      }[]
    | null
  >(null);

  const [internalConfirmedMatches, setInternalConfirmedMatches] = React.useState<
    | {
        transaction: CreateBankTransactionModel;
        matchedConnection: Connection;
        contributionPlanId?: number;
      }[]
    | null
  >(null);

  const [eventExpenseMatches, setEventExpenseMatches] = React.useState<
    | {
        transaction: CreateBankTransactionModel;
        matchedEvent: Event;
      }[]
    | null
  >(null);

  const connectionsAll = useSelector((state: RootState) => state.connections);
  const connections = connectionsAll.filter((c) => c.type === ConnectionType.B2C_CONNECTION);

  useEffect(() => {
    if (open) {
      const matched = matchMutationsToConnections(unassignedMutations, connections);
      setMatchedTransactions(
        matched.map(({ transaction, matchedConnection }) => ({
          mutation: transaction,
          matchedConnection,
        })),
      );
    }
  }, [open]);

  // const handleFileUpload = (files: FileList) => {
  //   if (files.length === 1) {
  //     const file = files[0];
  //     new RabobankParser(accountNumber).parse(file).then((parsed) => {
  //       const matched = [] as {
  //         mutation: CreateBankTransactionModel;
  //         matchedConnection?: Connection | undefined;
  //       }[]; // matchTransactionsToConnections(parsed, connections);
  //       setMatchedTransactions(matched);
  //       setTransactions(parsed);
  //       setStep(1);
  //     });
  //   } else {
  //     toast.error(t('toast.error.oneFile'));
  //   }
  // };

  const handleFinish = async (selectedGroupMutations: CreateBankTransactionModel[]) => {
    // Process internal mutation matches first
    if (internalMutationMatches && internalMutationMatches.length > 0) {
      try {
        const confirmedInternalMatches = internalMutationMatches.filter((match) => match.confirmed);

        if (confirmedInternalMatches.length > 0) {
          // Create internal transfers for confirmed matches
          await Promise.all(
            confirmedInternalMatches.map((match) =>
              bankAccountService.createInternalTransferFromMutations(
                match.mutation.mutationId!,
                match.counterpartMutation.mutationId,
                match.mutation.description,
              ),
            ),
          );

          // Update mutations state to mark them as processed
          onUpdateMutations((prevMutations) => {
            if (!prevMutations) return prevMutations;
            return prevMutations.map((mutation) => {
              const wasProcessed = confirmedInternalMatches.some(
                (match) =>
                  match.mutation.mutationId === mutation.mutationId ||
                  match.counterpartMutation.mutationId === mutation.mutationId,
              );
              return wasProcessed
                ? { ...mutation, bankTransactionCount: mutation.bankTransactionCount + 1 }
                : mutation;
            });
          });
        }
      } catch (error) {
        console.error('Error creating internal transfers:', error);
        // Continue with the flow even if internal transfer creation fails
      }
    }

    // Process event expense matches
    if (eventExpenseMatches && eventExpenseMatches.length > 0) {
      try {
        const personalExpenseTransactions: CreateBankTransactionModel[] = eventExpenseMatches
          .map((eventMatch) => {
            const { transaction, matchedEvent } = eventMatch;

            // Get participants who attended the event
            const attendingParticipants = matchedEvent.participants.filter(
              (participant) => participant.answer === EventParticipationAnswer.YES,
            );

            if (attendingParticipants.length > 0) {
              // Find connections for the attending participants
              const attendingConnections = attendingParticipants
                .map((participant) => connections.find((conn) => conn.userId === participant.id))
                .filter((conn): conn is Connection => conn !== undefined);

              // Edge case: Only create transaction if there are attending connections
              if (attendingConnections.length > 0) {
                // Calculate equal split with proper rounding to ensure total equals original amount
                const totalAmount = Math.abs(transaction.amount);
                const baseAmount = Math.floor(totalAmount / attendingConnections.length);
                const remainder = totalAmount - baseAmount * attendingConnections.length;

                // Create participations with proper rounding
                const participations = attendingConnections.map((connection, index) => {
                  // Distribute remainder cents to first N participants
                  const amount = baseAmount + (index < remainder ? 1 : 0);
                  return {
                    connectionId: +connection.id,
                    amount: transaction.amount < 0 ? -amount : amount, // Preserve original sign
                  };
                });

                // Create a single personal expense transaction with multiple participations
                return {
                  ...transaction,
                  transactionType: TransactionType.PERSONAL_EXPENSE,
                  amount: transaction.amount, // Keep original amount
                  participations,
                } as CreateBankTransactionModel;
              }
            }
            return null;
          })
          .filter((transaction): transaction is CreateBankTransactionModel => transaction !== null);

        // Create all transactions at once
        if (personalExpenseTransactions.length > 0) {
          await Promise.all(
            personalExpenseTransactions.map((tns) =>
              bankAccountService.createTransactionWithParticipations(tns),
            ),
          );

          // Update mutations state to mark them as processed
          onUpdateMutations((prevMutations) => {
            if (!prevMutations) return prevMutations;
            return prevMutations.map((mutation) => {
              const wasProcessed = personalExpenseTransactions.some(
                (tns) => tns.mutationId === mutation.mutationId,
              );
              return wasProcessed
                ? { ...mutation, bankTransactionCount: mutation.bankTransactionCount + 1 }
                : mutation;
            });
          });
        }
      } catch (error) {
        console.error('Error creating event expense transactions:', error);
        // Continue with the flow even if event expense creation fails
      }
    }

    // Filter out mutations that were already processed as internal transfers or event expenses
    const filteredSelectedGroupMutations = selectedGroupMutations.filter((mutation) => {
      const wasInternalTransfer = internalMutationMatches?.some(
        (internalMatch) =>
          internalMatch.mutation.mutationId === mutation.mutationId ||
          internalMatch.counterpartMutation.mutationId === mutation.mutationId,
      );
      const wasEventExpense = eventExpenseMatches?.some(
        (eventMatch) => eventMatch.transaction.mutationId === mutation.mutationId,
      );
      return !wasInternalTransfer && !wasEventExpense;
    });

    onParseSuccess(internalConfirmedMatches ?? [], filteredSelectedGroupMutations);
    setOpen(false); // close modal
    setStep(0); // reset step
  };

  const renderStepContent = () => {
    switch (step) {
      case 0:
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-primary-900">
              {t('component.modal.uploadFinancialFile.internalMutationMatchTitle')}
            </h3>
            <p className="text-primary-800 text-sm">
              {t('component.modal.uploadFinancialFile.internalMutationMatchSubtitle')}
            </p>
            <VerifyInternalMutationMatchesView
              unassignedMutations={unassignedMutations}
              onConfirm={(confirmedMatches) => {
                setInternalMutationMatches(confirmedMatches);
                setStep(1);
              }}
            />
          </div>
        );
      case 1:
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-primary-900">
              {t('component.modal.uploadFinancialFile.reviewTitle')}
            </h3>
            {/* You could display a preview or offer categorization or tagging here */}
            <p className="text-primary-800 text-sm">
              {t('component.modal.uploadFinancialFile.reviewSubtitle')}
            </p>
            <VerifyTransactionMatchesView
              matches={matchedTransactions?.filter((m) => m.matchedConnection)!}
              onConfirm={(confirmed) => {
                setInternalConfirmedMatches(confirmed);
                setParentConfirmedMatches(confirmed);
                setMatchedTransactions(
                  (prev) =>
                    prev?.map((m) => {
                      const found = confirmed.find((c) => c.transaction === m.mutation);
                      return found
                        ? { ...m, matchedConnection: found.matchedConnection }
                        : { ...m, matchedConnection: undefined };
                    }) ?? null,
                );
                // Check if we need to show the contribution plan matching step
                // For now, we'll always show it, but we could add logic here to check
                // if there are any contribution plans that could be matched
                setStep(2);
              }}
            />
          </div>
        );
      case 2:
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-primary-900">
              {t('component.modal.uploadFinancialFile.contributionPlanMatchTitle')}
            </h3>
            <p className="text-primary-800 text-sm">
              {t('component.modal.uploadFinancialFile.contributionPlanMatchSubtitle')}
            </p>
            <VerifyContributionPlanMatchesView
              matches={
                internalConfirmedMatches?.map(
                  (match: {
                    transaction: CreateBankTransactionModel;
                    matchedConnection: Connection;
                    contributionPlanId?: number;
                  }) => ({
                    transaction: match.transaction,
                    matchedConnection: match.matchedConnection,
                  }),
                ) ?? []
              }
              onConfirm={(contributionPlanMatches) => {
                // Update confirmed matches with contribution plan IDs
                const updatedMatches =
                  internalConfirmedMatches?.map(
                    (match: {
                      transaction: CreateBankTransactionModel;
                      matchedConnection: Connection;
                      contributionPlanId?: number;
                    }) => {
                      const planMatch = contributionPlanMatches.find(
                        (pm) =>
                          pm.transaction === match.transaction &&
                          pm.matchedConnection.id === match.matchedConnection.id,
                      );
                      return {
                        ...match,
                        contributionPlanId: planMatch?.contributionPlanId,
                      };
                    },
                  ) ?? [];
                setInternalConfirmedMatches(updatedMatches);
                setParentConfirmedMatches(updatedMatches);
                setStep(3);
              }}
            />
          </div>
        );
      case 3:
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-primary-900">
              {t('component.modal.uploadFinancialFile.eventExpenseMatchTitle')}
            </h3>
            <p className="text-primary-800 text-sm">
              {t('component.modal.uploadFinancialFile.eventExpenseMatchSubtitle')}
            </p>
            <VerifyEventExpenseMatchesView
              unmatched={
                matchedTransactions
                  ?.filter((m) => !m.matchedConnection)
                  .map((m) => m.mutation)
                  .filter(
                    (mutation) =>
                      !internalMutationMatches?.some(
                        (internalMatch) =>
                          internalMatch.mutation.mutationId === mutation.mutationId ||
                          internalMatch.counterpartMutation.mutationId === mutation.mutationId,
                      ),
                  ) ?? []
              }
              onConfirm={(eventMatches) => {
                setEventExpenseMatches(eventMatches);
                setStep(4);
              }}
            />
          </div>
        );
      case 4:
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-primary-900">
              {t('component.modal.uploadFinancialFile.finalStepTitle')}
            </h3>
            <p className="text-primary-800 text-sm">
              {t('component.modal.uploadFinancialFile.finalStepSubtitle')}
            </p>
            <HandleUnmatchedTransactionsView
              unmatched={
                matchedTransactions
                  ?.filter((m) => !m.matchedConnection)
                  .map((m) => m.mutation)
                  .filter((mutation) => {
                    const wasInternalTransfer = internalMutationMatches?.some(
                      (internalMatch) =>
                        internalMatch.mutation.mutationId === mutation.mutationId ||
                        internalMatch.counterpartMutation.mutationId === mutation.mutationId,
                    );
                    const wasEventExpense = eventExpenseMatches?.some(
                      (eventMatch) => eventMatch.transaction.mutationId === mutation.mutationId,
                    );
                    return !wasInternalTransfer && !wasEventExpense;
                  }) ?? []
              }
              onConfirm={(selected) => {
                console.log(selected);
                handleFinish(selected);
              }}
            />
          </div>
        );
      default:
        console.warn(`Unknown step: ${step}`);
        return null;
    }
  };

  return (
    <ModalLayout
      open={open}
      setOpen={setOpen}
      closeButton
      className="z-50 mx-6 my-8 w-full rounded-[20px] bg-secondary-50 p-8 sm:max-w-[700px]">
      {renderStepContent()}
    </ModalLayout>
  );
}
